import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";

export interface SubscriptionRow {
  id: string;
  user_id: string;
  plan: string | null;
  status: string;
  paddle_customer_id: string | null;
  paddle_subscription_id: string | null;
  paddle_price_id: string | null;
  price_id: string | null;
  product_id: string | null;
  environment: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export const useSubscription = () =>
  useQuery({
    queryKey: ["subscription"],
    queryFn: async (): Promise<SubscriptionRow | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as SubscriptionRow[];
      const env = getPaddleEnvironment();
      const sameEnv = rows.filter((r) => r.environment === env);
      // Prefer the current environment, but never lock out a paying customer
      // whose row was stamped with the other environment.
      return (
        sameEnv.find(isActive) ??
        rows.find(isActive) ??
        sameEnv[0] ??
        rows[0] ??
        null
      );
    },
  });

export const isActive = (sub?: SubscriptionRow | null) =>
  !!sub && ["active", "trialing", "past_due"].includes(sub.status);
